//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import {
  ClientPurchaseOptionsSummary,
  GetClientPurchaseOptionsSummaryRequest,
  GetClientPurchaseOptionsSummaryResponse,
  GetSamplingOpportunitiesResponse,
  SamplingOpportunitiesResponse,
} from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiModel';
import { deserializeOpportunities } from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiUtils';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const API_V1 = 'v1';
const API_V2 = 'v2';

const samplingFetch = (
  endpoint: string,
  version: typeof API_V1 | typeof API_V2,
  init?: RequestInit,
): Promise<ISerializedResponse> =>
  serializedGatewayFetch(
    'sampling_gateway',
    `/api/${version}/clients${endpoint}`,
    init,
  );

/**
 * Get purchase options
 *
 * GET /api/v2/clients/opportunities
 */
export const getSamplingOpportunities = async (
  init?: RequestInit,
): Promise<SamplingOpportunitiesResponse> => {
  const queryParams = new URLSearchParams();
  queryParams.append('productTypes', 'DEALS');
  queryParams.append('productTypes', 'SAMPLING_UNIT');
  queryParams.append('includeFulfillmentOptions', 'true');

  const res = await samplingFetch(
    `/opportunities?${queryParams.toString()}`,
    API_V2,
    {
      ...init,
      method: 'GET',
    },
  );

  const samplingOpportunitiesResponse =
    await res.json<GetSamplingOpportunitiesResponse>((res) => ({
      data: {
        ...res.data,
        opportunities: deserializeOpportunities(res.data.opportunities),
      },
    }));

  return samplingOpportunitiesResponse.data;
};

/**
 * Get purchase options summary
 *
 * GET /api/v1/clients/opportunities/summary
 */
export const getSamplingOpportunitiesSummary = async (
  req: GetClientPurchaseOptionsSummaryRequest,
  init?: RequestInit,
): Promise<ClientPurchaseOptionsSummary> => {
  const params = makeParams(req);
  const res = await samplingFetch(`/opportunities/summary?${params}`, API_V1, {
    ...init,
    method: 'GET',
  });

  const summaryResponse =
    await res.json<GetClientPurchaseOptionsSummaryResponse>();
  return summaryResponse.data;
};
